/*
 * @Author: zhang lin
 * @Date: 2022-08-22 16:14:17
 * @LastEditors: zhang lin
 * @LastEditTime: 2023-02-28 09:21:09
 */
import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Form, Input, DatePicker, Switch, message, Tooltip } from 'sup-ui';
import { WrappedFormUtils } from 'sup-ui/lib/form/Form';
import moment from 'moment';
import Modal from 'root/components/CommonModal';
import Icon from 'root/components/Icon';
import messages from './messages';
import styles from './index.less';
import { formatDate } from 'root/utils/utils';

import { setSystemNotifications, getSystemNotifications } from 'root/services/notice';
import { IPlatformTimeType } from 'root/components/EditSysTime/types';

const platformFormItemLayout = {
  labelCol: { span: 5 },
  wrapperCol: { span: 19 },
};

interface PropsType {
  form: WrappedFormUtils;
  suposStatus: string; // supos状态
  record: IPlatformTimeType; // 系统时间
}

interface infoType {
  content?: string;
  expiresTime?: number;
  status?: boolean;
}

const { confirm } = Modal;

const SystemNotice = (props: PropsType) => {
  const intl = useIntl();
  const { suposStatus, record, form } = props;
  const { getFieldDecorator, validateFields } = props.form;
  const { TextArea } = Input;
  const [dataInfo, setDataInfo] = useState<infoType>({});
  const [visible, setVisible] = useState<boolean>(false);

  // 不允许选择的日期
  const disabledStartDate = (current: any) => {
    return current && current < moment(record.systemTime?.slice(0, 10));
  };

  const range = (start: any, end: any) => {
    const result = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  };

  // 不允许选择的时间
  const disabledTime = (current: any) => {
    if (new Date(current) > new Date(moment(record.systemTime) as any)) return;
    if (moment(current._d).isSame(record.systemTime, 'd')) {
      const date = new Date(record.systemTime);
      const h = date.getHours();
      const m = date.getMinutes();
      const s = date.getSeconds();

      return {
        disabledHours: () => range(0, h),
        disabledMinutes: () => range(0, m),
        disabledSeconds: () => range(0, s),
      };
    }
  };

  // 获取配置信息
  useEffect(() => {
    if (['running', 'run_with_high_risk', 'run_with_low_risk'].includes(suposStatus)) {
      getData();
    }
  }, [suposStatus]);
  const getData = () => {
    getSystemNotifications().then((res: any) => {
      setDataInfo(res?.data);
    });
  };

  // 点击开关
  const changeSwitch = (checked: boolean) => {
    // 关闭配置
    if (!checked) {
      confirm({
        title: intl.formatMessage(messages.closeTip),
        onOk: () => {
          const body = {
            content: dataInfo.content,
            expiresTime: dataInfo.expiresTime,
            status: false,
          };
          setData(body);
        },
      });
      return;
    }
    setVisible(!visible);
  };

  const setData = (body: any) => {
    setSystemNotifications(body).then(() => {
      setVisible(false);
      getData();
    });
  };

  const onOk = () => {
    validateFields((err: any, values: any) => {
      if (err) {
        return;
      }
      const { content, expiresTime } = values;
      const date = moment(expiresTime).valueOf();

      // 内容为空格验证
      if (!content.trim()) {
        message.error(intl.formatMessage(messages.contentSpace));
        return;
      }

      // 对比选择的时间和系统时间
      if (date < new Date(record.systemTime).getTime()) {
        message.error(intl.formatMessage(messages.dateError));
        return;
      }
      // 开启通知配置
      const body = {
        content,
        expiresTime: date,
        status: true,
      };
      setData(body);
    });
  };
  const onCancel = () => {
    setVisible(false);
  };

  // 禁止换行
  const onKeyDown = (e: any) => {
    if (e.keyCode === 13) {
      e.cancelBubble = true;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // 避免粘贴带换行
  const onInput = (e: any) => {
    const text = e?.target?.value;

    const reg = /\n/;
    if (reg.test(text)) {
      const newText = text.replace(/\n/g, '');
      form.setFieldsValue({ content: newText });
      e.target.value = newText;
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.container}>
        <div>
          <span className={styles.title}>{intl.formatMessage(messages.systemNotification)}</span>

          {['running', 'run_with_high_risk', 'run_with_low_risk'].includes(suposStatus) ? (
            <Switch
              className={styles.switch}
              size="small"
              checked={dataInfo.status}
              defaultChecked={dataInfo.status}
              onClick={changeSwitch}
            />
          ) : (
            <div className={styles.tip}>
              <Icon type="tip16" className={styles.tipIcon} />
              <span>{intl.formatMessage(messages.statusTip)}</span>
            </div>
          )}

          {dataInfo.status && (
            <Icon
              type="edit161"
              className={styles.editIcon}
              onClick={() => {
                setVisible(!visible);
              }}
            />
          )}
        </div>
        {['running', 'run_with_high_risk', 'run_with_low_risk'].includes(suposStatus) && (
          <ul className={styles.list}>
            <li className={styles.time}>
              <p>
                {intl.formatMessage(messages.endTime)} : {dataInfo.expiresTime ? '' : '----'}
              </p>
              <p>{formatDate(dataInfo.expiresTime)}</p>
            </li>
            <li className={styles.noticeContent}>
              {intl.formatMessage(messages.noticeContent)} :
              <Tooltip
                title={<p className={styles.tooltip}>{dataInfo.content}</p>}
                overlayClassName={styles.tooltipBox}
              >
                <div>
                  <Icon type="text16" className={styles.icon} />
                </div>
              </Tooltip>
            </li>
          </ul>
        )}

        <Modal
          className={styles.editSysTimeBox}
          title={intl.formatMessage(messages.title)}
          visible={visible}
          onOk={onOk}
          height="auto"
          onCancel={onCancel}
          destroyOnClose
        >
          <Form {...platformFormItemLayout}>
            <div className={styles.form}>
              <div className={styles.item}>
                <Form.Item label={intl.formatMessage(messages.noticeContent)} colon={false}>
                  {getFieldDecorator('content', {
                    initialValue: dataInfo.content,
                    rules: [
                      {
                        required: true,
                        message: intl.formatMessage(messages.noticeNull),
                      },
                    ],
                  })(
                    <TextArea
                      placeholder={intl.formatMessage(messages.notificationTip)}
                      maxLength={100}
                      onKeyDown={onKeyDown}
                      onInput={onInput}
                    />,
                  )}
                </Form.Item>
              </div>
              <div className={styles.item}>
                <Form.Item
                  label={intl.formatMessage(messages.notificationCloseTime)}
                  className={styles.date}
                  colon={false}
                >
                  {getFieldDecorator('expiresTime', {
                    initialValue: dataInfo?.expiresTime ? moment(dataInfo?.expiresTime) : null,
                    rules: [
                      {
                        required: true,
                        message: intl.formatMessage(messages.dateNull),
                      },
                    ],
                  })(
                    <DatePicker
                      style={{ width: '100%' }}
                      format="YYYY[年]M[月]D[日] HH:mm"
                      disabledDate={disabledStartDate}
                      disabledTime={disabledTime}
                      showTime={{ format: 'HH:mm' }}
                      placeholder={intl.formatMessage(messages.timePlaceholder)}
                      showToday={false}
                    />,
                  )}
                </Form.Item>
              </div>
            </div>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default Form.create()(SystemNotice);
